import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import { canvasListPageStyles } from 'apps/canvas/canvas-list-page/CanvasListPageStyles';
import BaseTypography from 'core/components/typography/BaseTypography';
import { useDispatch, useSelector } from 'react-redux';
import {
	createCanvasApiStageSelector,
	createCanvasAsync,
	listCanvases,
	resetCanvasSettings,
} from 'core/store/reducers/canvasReducer/CanvasSlice';
import { PreloadChannels } from 'core/constants/enums/PreloadChannels.ts';
import { ConditionalLayer } from 'core/layouts/ui-blocks/ReusableComponents';
import { colorPalette } from 'core/layouts/ui-blocks/ColorPalette';
import ProfileButton from 'core/layouts/components/profile-button/ProfileButton';
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { setSelectedCharacter } from 'core/store/reducers/characterStudioReducer/CharaterStudioSlice';
import { getUserProfileAsync } from 'core/store/reducers/appReducer/AppSlice';
import { ReactComponent as CanvasIcon } from 'core/assets/svg/canvas-page/canvas.svg';
import { ReactComponent as RefreshIcon } from 'core/assets/svg/home-page/refresh.svg';
import { ReactComponent as RenderNetIcon } from 'core/assets/svg/navbar/rendernet-icon.svg';

export function ActionButton({ title, icon, onClick, disabled }) {
	return (
		<Box
			id="action-button"
			sx={[
				reusableStyles.flexRowWithGap(12),
				canvasListPageStyles.desktopAppToolbarActionButton(disabled),
			]}
			onClick={onClick}
		>
			{icon}
			<BaseTypography
				id={`${title}-button`}
				key={`${title}-button`}
				variant="t2_v2_medium"
				color="colors.light3"
			>
				{title}
			</BaseTypography>
		</Box>
	);
}

export default function DesktopAppToolbar() {
	const dispatch = useDispatch();

	const createCanvasApiStage = useSelector(createCanvasApiStageSelector);

	const [updateProgress, setUpdateProgress] = useState(null);

	const [isUpdateRequired, setIsUpdateRequired] = useState(false);

	const isUpdateComplete = Number(updateProgress) === 100.0;

	useEffect(() => {
		let interval;
		if (window.electron) {
			window.electron.ipcRenderer.on(PreloadChannels.UPDATE_DOWNLOAD_PROGRESS, (progressData) => {
				const { progress, isMajorRelease } = progressData;
				if (isMajorRelease) setIsUpdateRequired(true);
				if (progress != null) setUpdateProgress(Number(progress).toFixed(2));
				if (progress === 100 || progress == null) clearInterval(interval);
			});
			interval = setInterval(() => {
				window.electron.ipcRenderer.send(PreloadChannels.UPDATE_DOWNLOAD_PROGRESS, {});
			}, 500);
		}
		return () => {
			if (interval) {
				clearInterval(interval);
			}
			window.electron.ipcRenderer.removeAllListeners(PreloadChannels.UPDATE_DOWNLOAD_PROGRESS);
		};
	}, []);

	return (
		<>
			<ConditionalLayer isShown={isUpdateRequired && updateProgress != null}>
				<Box
					sx={{
						position: 'absolute',
						width: '100%',
						height: '100%',
						zIndex: '100',
						background: colorPalette.loadingBackground,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<BaseTypography variant="t1_bold" color={colorPalette.light3}>
						{isUpdateComplete
							? 'Updates downloaded. Please restart Affogato to apply them.'
							: 'Please wait until Affogato is being updated...'}
					</BaseTypography>
				</Box>
			</ConditionalLayer>
			<Box sx={[reusableStyles.flexColWithGap(8), { width: '100%' }]}>
				<Box
					id="navbar-container"
					sx={[reusableStyles.flexRow, canvasListPageStyles.desktopAppToolbarContainer]}
				>
					<RenderNetIcon style={{ color: colorPalette.light2 }} />
					<ProfileButton />
				</Box>
				<ConditionalLayer isShown={updateProgress != null}>
					<Box
						sx={{
							width: '100%',
							padding: '24px',
							paddingTop: '0px',
							paddingBottom: '8px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: '12px',
						}}
					>
						<BaseTypography variant="b1_v2_medium" color="colors.light3">
							{updateProgress == null
								? ''
								: isUpdateComplete
									? 'Update has been downloaded. Restart to apply updates.'
									: `Update Download Progress: ${updateProgress}%`}
						</BaseTypography>
						<ConditionalLayer isShown={!isUpdateComplete}>
							<CircularProgress
								style={{
									width: '25px',
									height: '25px',
									color: colorPalette.light3,
								}}
								variant="indeterminate"
							/>
						</ConditionalLayer>
					</Box>
				</ConditionalLayer>
				<Box
					id="desktop-app-toolbar"
					sx={[reusableStyles.flexRowWithGap(16), canvasListPageStyles.desktopAppToolbar]}
				>
					<ActionButton
						title={createCanvasApiStage === ApiStage.LOADING ? 'Creating...' : 'Create new Canvas'}
						icon={<CanvasIcon />}
						onClick={() => {
							dispatch(createCanvasAsync());
							dispatch(resetCanvasSettings());
							dispatch(setSelectedCharacter(null));
						}}
						disabled={createCanvasApiStage === ApiStage.LOADING}
					/>
					<Box
						id="refresh-button"
						sx={canvasListPageStyles.refreshButton}
						onClick={() => {
							dispatch(getUserProfileAsync());
							dispatch(listCanvases());
						}}
					>
						<RefreshIcon />
					</Box>
				</Box>
			</Box>
		</>
	);
}
